import { GripVertical, TriangleAlert } from '@orgus/ui/icons';
import { AnimatePresence, LayoutGroup, Reorder, motion } from 'motion/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import type { Id, Landing, LandingQueue, MachineLoadState } from '@orgus/protocol';
import { Badge, Button, ErrorState, Pressable, RowStatus, Tooltip, cn, formatDuration, formatRelative, plural, useNow } from '@orgus/ui';
import { useExt } from '../../lib/store.ts';
import { MACHINE_LOAD_STATE_EXT } from '../machine-load/model.ts';
import { findWaitingInfo } from '../machine-load/cpuWaiting.ts';
import { WaitingForCpuBadge } from '../machine-load/WaitingForCpuBadge.tsx';
import { reorderQueue } from './api.ts';
import { boardColumns, etaText, landingCardNote, landingColumnCount, landingRowStatus, moveInOrder, sameOrder } from './model.ts';
import type { LandingCard, LandingColumn } from './model.ts';

const SPRING = { type: 'spring', stiffness: 360, damping: 34, mass: 0.9 } as const;

/** What each column means, said once under its title so a card never has to repeat it. */
const COLUMN_NOTE: Record<LandingColumn['stage'], string> = {
  change: 'In landing order, first lands next',
  verify: 'Rebased, checks running',
  push: 'Verified, going onto main',
  deploy: 'On main, deploying',
  landed: 'On main, newest first',
};

/**
 * One queue as a board (Queued, Verifying, Pushing, Deploying, Landed), drawn like the
 * Pipelines board: sunken wells, cards with one status, a note for their column and the time.
 * Queued cards drag to reorder (or Alt+Up / Alt+Down on a focused card); each move is sent to
 * the hub and audited, and a refused move shows its error above the board.
 */
export function LandingBoard({
  queue,
  landings,
  onOpen,
  selectedId,
  machineName,
  landedNote,
}: {
  queue: LandingQueue;
  landings: readonly Landing[];
  onOpen: (id: Id) => void;
  selectedId?: Id | null;
  machineName: (id: Id | null) => string;
  /** The Landed column's note in place of its default: "41 landed, typically 4m 20s". */
  landedNote?: string;
}) {
  const now = useNow(1000);
  const cols = useMemo(() => boardColumns(queue, landings, now), [queue, landings, now]);
  const [order, setOrderState] = useState<Id[]>(queue.order);
  // The order as dragged so far, read when the drag ends (outside React's render).
  const orderRef = useRef<Id[]>(queue.order);
  const setOrder = (next: Id[]) => {
    orderRef.current = next;
    setOrderState(next);
  };
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const dragging = useRef(false);
  const queued = cols[0]!.cards;
  const byId = useMemo(() => Object.fromEntries(queued.map((c) => [c.landing.id, c])), [queued]);

  // Follow the hub's order unless a move of ours is on its way.
  useEffect(() => {
    if (!dragging.current && !busy) setOrder(queue.order);
  }, [queue.order, busy]);

  const commit = async (next: Id[]) => {
    if (sameOrder(next, queue.order)) return;
    setOrder(next);
    setBusy(true);
    setError(null);
    try {
      await reorderQueue(queue.id, next);
    } catch (e) {
      setOrder(queue.order);
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  const onKey = (e: KeyboardEvent, id: Id) => {
    if (!e.altKey || (e.key !== 'ArrowUp' && e.key !== 'ArrowDown')) return;
    e.preventDefault();
    void commit(moveInOrder(order, id, e.key === 'ArrowUp' ? -1 : 1));
  };

  const visibleOrder = order.filter((id) => byId[id]);

  return (
    <LayoutGroup id={`landing:${queue.id}`}>
      {error != null && (
        <ErrorState
          variant="inline"
          className="mb-3"
          error={error}
          action={
            <Button variant="ghost" size="sm" onClick={() => setError(null)}>
              Dismiss
            </Button>
          }
        />
      )}
      {/* The board's own heading, so the column headings (h3) never follow the page's h1 directly. */}
      <h2 className="sr-only">Landing stages</h2>
      <div className="flex flex-col gap-(--board-column-gap) md:flex-row md:items-start md:overflow-x-auto md:pb-1" role="list" aria-label="Landing stages">
        {cols.map((col, ci) => {
          const count = landingColumnCount(col, queue.landedCount);
          // An empty stage keeps only its header (and on a phone, where the stages stack, it goes).
          const empty = col.stage !== 'change' && !col.cards.length;
          return (
            // A div: a <section> may not take the listitem role.
            <div
              key={col.stage}
              role="listitem"
              aria-label={`${col.title}, ${count.label}`}
              className={cn('flex min-w-48 flex-col gap-(--board-card-gap) rounded-card border border-border-subtle bg-(--board-column-bg) p-2 md:max-w-90 md:flex-1 md:basis-0', empty && 'max-md:hidden')}
            >
              <header className="flex flex-col px-2">
                <span className="flex h-control items-center gap-2">
                  <span className="text-caption text-fg-subtle num">{ci + 1}</span>
                  <h3 className="min-w-0 truncate text-ui font-medium text-fg">{col.title}</h3>
                  <Tooltip content={count.label}>
                    <span tabIndex={0} role="img" className="ml-auto text-caption text-fg-subtle num" aria-label={count.label}>
                      {count.n}
                    </span>
                  </Tooltip>
                </span>
                <span className={cn('truncate text-caption text-fg-subtle', !empty && 'pb-1')}>{col.stage === 'landed' && landedNote ? landedNote : COLUMN_NOTE[col.stage]}</span>
              </header>
              {col.stage === 'change' ? (
                <>
                  <p id={`reorder-help-${queue.id}`} className="sr-only">
                    Drag a card, or press Alt with the up or down arrow, to change the landing order.
                  </p>
                  <Reorder.Group as="ol" axis="y" values={visibleOrder} onReorder={setOrder} className="flex flex-col gap-(--board-card-gap)">
                    {visibleOrder.map((id, i) => {
                      const card = byId[id]!;
                      return (
                        <Reorder.Item
                          key={id}
                          value={id}
                          layoutId={`landing:${id}`}
                          onDragStart={() => {
                            dragging.current = true;
                          }}
                          onDragEnd={() => {
                            window.setTimeout(() => {
                              dragging.current = false;
                            }, 0);
                            void commit(orderRef.current);
                          }}
                          transition={SPRING}
                          className="list-none"
                        >
                          <Card
                            card={{ ...card, eta: card.eta ? { ...card.eta, position: i + 1 } : null }}
                            position={i + 1}
                            busy={queue.activeId != null}
                            now={now}
                            paused={queue.paused}
                            selected={selectedId === id}
                            draggable
                            onOpen={() => !dragging.current && onOpen(id)}
                            onKeyDown={(e) => onKey(e, id)}
                            describedBy={`reorder-help-${queue.id}`}
                            machineName={machineName}
                          />
                        </Reorder.Item>
                      );
                    })}
                  </Reorder.Group>
                  {!visibleOrder.length && <p className="px-2 pb-2 text-caption text-fg-subtle">Nothing waiting</p>}
                </>
              ) : (
                <>
                  <AnimatePresence mode="popLayout" initial={false}>
                    {col.cards.map((c) => (
                      <motion.div
                        key={c.landing.id}
                        layoutId={`landing:${c.landing.id}`}
                        layout
                        initial={{ opacity: 0, scale: 0.98 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.98, transition: { duration: 0.12 } }}
                        transition={SPRING}
                      >
                        <Card card={c} stage={col.title} now={now} paused={queue.paused} selected={selectedId === c.landing.id} onOpen={() => onOpen(c.landing.id)} machineName={machineName} />
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </>
              )}
            </div>
          );
        })}
      </div>
    </LayoutGroup>
  );
}

/** A card's line without the stage word its column already says: "Pushing 7777af6 to main" reads "7777af6 to main". */
export function cardNote(note: string, stage?: string): string {
  if (!stage) return note;
  const word = `${stage} `;
  return note.toLowerCase().startsWith(word.toLowerCase()) ? note.slice(word.length) : note;
}

/**
 * One landing: its title (two lines at most), its branch, the line for its column, then its
 * status when that is the news, at most one chip (conflicts so far) and the time.
 */
function Card({
  card,
  position = 0,
  busy = false,
  stage,
  now,
  paused,
  selected,
  draggable,
  onOpen,
  onKeyDown,
  describedBy,
  machineName,
}: {
  card: LandingCard;
  position?: number;
  busy?: boolean;
  /** The column's title: at work, the status names it ("Pushing"), so the card reads as its column. */
  stage?: string;
  now: number;
  paused: boolean;
  selected: boolean;
  draggable?: boolean;
  onOpen: () => void;
  onKeyDown?: (e: KeyboardEvent) => void;
  describedBy?: string;
  machineName: (id: Id | null) => string;
}) {
  const l = card.landing;
  const loadSlice = useExt<MachineLoadState>(MACHINE_LOAD_STATE_EXT);
  const cpuInfo = findWaitingInfo(loadSlice, { branch: l.source.branch, what: `orgus land ${l.source.branch}` });
  const active = l.status === 'active';
  const landed = l.status === 'landed';
  const elapsed = l.startedAt != null ? (l.endedAt ?? now) - l.startedAt : null;
  // The column names the stage, so a card never says it again: an active card's line is where it
  // runs ("7777af6 to main on studio"), a landed card's is its version beside the done icon.
  const rawNote = cpuInfo?.isWaiting ? cpuInfo.label : landingCardNote(l, position, busy, machineName(l.machineId));
  const note = cardNote(rawNote, stage);
  const eta = etaText(card.eta, now, paused);
  const time = landed ? (l.endedAt ? formatRelative(l.endedAt, now) : '') : active ? (elapsed != null ? formatDuration(elapsed) : '') : eta.replace(/^Lands in about /, 'ETA ');
  return (
    <Pressable
      data-card=""
      onClick={onOpen}
      onKeyDown={onKeyDown}
      aria-describedby={describedBy}
      className={cn(
        'group flex w-full flex-col gap-2 rounded-card border border-border bg-surface p-3 shadow-elevation-1 hover:border-border-strong',
        draggable && 'cursor-grab active:cursor-grabbing',
        selected && 'ring-2 ring-focus ring-offset-1 ring-offset-sunken',
      )}
    >
      <span className="flex items-start gap-1">
        {draggable && <GripVertical className="size-4 shrink-0 text-fg-faint group-hover:text-fg-subtle" aria-hidden />}
        <span className="line-clamp-2 min-w-0 flex-1 text-ui font-medium text-fg">{l.title}</span>
      </span>
      <span className="flex min-w-0 flex-col gap-1">
        <span className="truncate font-mono text-caption text-fg-subtle" data-meta="" title={`${l.source.branch}, from ${l.submittedBy}`}>
          {l.source.branch}
        </span>
        {cpuInfo?.isWaiting ? (
          <WaitingForCpuBadge placeInLine={cpuInfo.placeInLine} estimateMinutes={cpuInfo.estimateMinutes} work={cpuInfo.work} />
        ) : (
          !landed && (
            <span className={cn('line-clamp-2 text-caption', active ? 'text-fg-muted' : 'text-fg-subtle')} data-meta="" title={note}>
              {note}
            </span>
          )
        )}
      </span>
      <span className="flex min-w-0 items-center gap-2">
        {active && <RowStatus {...landingRowStatus(l)} hideLabel focusable={false} />}
        {landed && (
          <span className="flex min-w-0 items-center gap-2">
            <RowStatus {...landingRowStatus(l)} hideLabel focusable={false} />
            {l.version && <span className="truncate font-mono text-caption text-fg-muted">{l.version}</span>}
          </span>
        )}
        {l.conflicts > 0 && !landed && (
          <Tooltip content={`${plural(l.conflicts, 'conflict')} so far: rebased again in a fresh checkout`}>
            <Badge data-chip="" tone="waiting" size="sm" icon={<TriangleAlert />} tabIndex={-1} className="shrink-0">
              {plural(l.conflicts, 'conflict')}
            </Badge>
          </Tooltip>
        )}
        {time && (
          <span className="ml-auto shrink-0 text-caption text-fg-subtle num" title={!active && !landed ? eta : undefined}>
            {time}
          </span>
        )}
      </span>
    </Pressable>
  );
}
